import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Building2, MapPin, Search, Loader2 } from 'lucide-react';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { assignEdificiosToUser } from '@/services/users.service';

const AssignPortfoliosModal = ({ isOpen, onClose, user, onSuccess }) => {
  const { toast } = useToast();
  const [edificios, setEdificios] = useState([]);
  const [selectedEdificios, setSelectedEdificios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Cargar edificios y edificios asignados al usuario
  useEffect(() => {
    if (isOpen && user) {
      loadEdificios();
      setSelectedEdificios(user.edificiosAsignados || []);
    }
  }, [isOpen, user]);

  const loadEdificios = async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchEdificios({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });
      setEdificios(data || []);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los portafolios',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEdificio = (edificioId) => {
    setSelectedEdificios((prev) =>
      prev.includes(edificioId)
        ? prev.filter((id) => id !== edificioId)
        : [...prev, edificioId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEdificios.length === filteredEdificios.length) {
      setSelectedEdificios([]);
    } else {
      setSelectedEdificios(filteredEdificios.map((e) => e._id));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await assignEdificiosToUser(user._id, selectedEdificios);
      toast({
        title: 'Éxito',
        description: `Portafolios asignados correctamente a ${user.nombre}`,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error al asignar portafolios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron asignar los portafolios',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedEdificios([]);
    onClose();
  };

  // Filtrar edificios por búsqueda
  const filteredEdificios = edificios.filter((edificio) => {
    const search = searchTerm.toLowerCase();
    return (
      edificio.nombre?.toLowerCase().includes(search) ||
      edificio.ciudad?.toLowerCase().includes(search) ||
      edificio.direccion?.toLowerCase().includes(search)
    );
  });

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Asignar Portafolios a {user.nombre}
          </DialogTitle>
          <DialogDescription>
            Selecciona los portafolios que {user.nombre} podrá gestionar.
            {user.role === 'admin' && (
              <span className="block mt-1 text-amber-600 font-medium">
                ⚠️ Los administradores tienen acceso a todos los portafolios por defecto
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Barra de búsqueda y controles */}
        <div className="space-y-3 py-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, ciudad o dirección..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="select-all"
                checked={
                  filteredEdificios.length > 0 &&
                  selectedEdificios.length === filteredEdificios.length
                }
                onCheckedChange={handleSelectAll}
              />
              <label
                htmlFor="select-all"
                className="text-sm font-medium cursor-pointer"
              >
                Seleccionar todos ({filteredEdificios.length})
              </label>
            </div>

            <Badge variant="outline" className="text-sm">
              {selectedEdificios.length} seleccionado(s)
            </Badge>
          </div>
        </div>

        {/* Lista de edificios */}
        <div className="flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredEdificios.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {searchTerm
                  ? 'No se encontraron portafolios con ese criterio'
                  : 'No hay portafolios disponibles'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AnimatePresence>
                {filteredEdificios.map((edificio, index) => {
                  const isSelected = selectedEdificios.includes(edificio._id);
                  return (
                    <motion.div
                      key={edificio._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className={`
                        flex items-start space-x-3 p-4 border rounded-lg cursor-pointer
                        transition-all duration-200
                        ${
                          isSelected
                            ? 'bg-primary/10 border-primary shadow-sm'
                            : 'hover:bg-gray-50 border-gray-200'
                        }
                      `}
                      onClick={() => handleToggleEdificio(edificio._id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleEdificio(edificio._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="font-medium text-sm truncate">
                            {edificio.nombre}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{edificio.ciudad}</span>
                        </div>
                        {edificio.direccion && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {edificio.direccion}
                          </p>
                        )}
                        {edificio.tipoLogistica && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {edificio.tipoLogistica}
                          </Badge>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Footer con botones */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {selectedEdificios.length > 0 && (
              <span>
                {selectedEdificios.length} portafolio(s) seleccionado(s)
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isLoading}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar Asignación'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPortfoliosModal;
